import { createSelector } from 'reselect';
import { IMarriage, IMarriagesState } from 'types/marriages.types';
import { IMarriagesFormData } from 'views/public/MarriagesView/components/MarriagesForm/types';

const getItems = (state: { marriages: IMarriagesState }) => state.marriages.items;
const getSelectedItem = (state: { marriages: IMarriagesState }) => state.marriages.selectedItem;
const getCementeries = (state: { marriages: IMarriagesState }) => state.marriages.cementeries;
const getCivilStates = (state: { marriages: IMarriagesState }) => state.marriages.civilStates;
const getIdCardTypes = (state: { marriages: IMarriagesState }) => state.marriages.idCardTypes;
const getTribes = (state: { marriages: IMarriagesState }) => state.marriages.tribes;

export const marriagesSelector = createSelector([getItems], (marriages) =>
  marriages.map((marriage: IMarriage) => ({
    ...marriage,
    fullName: `${marriage.groom.lastName || ''} ${marriage.groom.firstName || ''} y ${
      marriage.bride.lastName || ''
    } ${marriage.bride.firstName || ''}`,
  })),
);

export const marriageFormSelector = createSelector(
  [getSelectedItem],
  (marriage): IMarriagesFormData => ({
    groom: {
      id: marriage?.groom?.id || 0,
      firstName: marriage?.groom?.firstName || '',
      lastName: marriage?.groom?.lastName || '',
      idCardType: marriage?.groom?.idCardType,
      idCard: marriage?.groom?.idCard || '',
      civilState: marriage?.groom?.civilState || '',
      tribe: marriage?.groom?.tribe || '',
      address: marriage?.groom?.address || '',
      apartment: marriage?.groom?.apartment || '',
      locality: marriage?.groom?.locality || '',
      country: marriage?.groom?.country || '',
      birthplace: marriage?.groom?.birthplace || '',
      birthdate: marriage?.groom?.birthdate || '',
      areaCode: marriage?.groom?.areaCode || '',
      phone: marriage?.groom?.phone || '',
      email: marriage?.groom?.email || '',
      community: marriage?.groom?.community || '',
      job: marriage?.groom?.job || '',
      parents: {
        father: {
          name: marriage?.groom?.parents?.father?.name || '',
          lastName: marriage?.groom?.parents?.father?.lastName || '',
          birthplace: marriage?.groom?.parents?.father?.birthplace || '',
          birthdate: marriage?.groom?.parents?.father?.birthdate || '',
          idCardType: marriage?.groom?.parents?.father.idCardType,
          idCard: marriage?.groom?.parents?.father?.idCard || '',
          civilState: marriage?.groom?.parents?.father?.civilState || '',
          tribe: marriage?.groom?.parents?.father?.tribe || '',
          alive: marriage?.groom?.parents?.father?.alive || true,
          cementery: marriage?.groom?.parents?.father?.cementery || '',
        },
        mother: {
          name: marriage?.groom?.parents?.mother?.name || '',
          lastName: marriage?.groom?.parents?.mother?.lastName || '',
          birthplace: marriage?.groom?.parents?.mother?.birthplace || '',
          birthdate: marriage?.groom?.parents?.mother?.birthdate || '',
          idCardType: marriage?.groom?.parents?.mother.idCardType,
          idCard: marriage?.groom?.parents?.mother?.idCard || '',
          civilState: marriage?.groom?.parents?.mother?.civilState || '',
          tribe: marriage?.groom?.parents?.mother?.tribe || '',
          alive: marriage?.groom?.parents?.mother?.alive || true,
          cementery: marriage?.groom?.parents?.mother?.cementery || '',
        },
        weddingTemple: marriage?.groom?.parents?.weddingTemple || '',
        weddingDate: marriage?.groom?.parents?.weddingDate || '',
      },
      grandParents: {
        paternalFather: {
          name: marriage?.groom?.grandParents?.paternalFather?.name || '',
          lastName: marriage?.groom?.grandParents?.paternalFather?.lastName || '',
          birthplace: marriage?.groom?.grandParents?.paternalFather?.birthplace || '',
        },
        paternalMother: {
          name: marriage?.groom?.grandParents?.paternalMother?.name || '',
          lastName: marriage?.groom?.grandParents?.paternalMother?.lastName || '',
          birthplace: marriage?.groom?.grandParents?.paternalMother?.birthplace || '',
        },
        maternalFather: {
          name: marriage?.groom?.grandParents?.maternalFather?.name || '',
          lastName: marriage?.groom?.grandParents?.maternalFather?.lastName || '',
          birthplace: marriage?.groom?.grandParents?.maternalFather?.birthplace || '',
        },
        maternalMother: {
          name: marriage?.groom?.grandParents?.maternalMother?.name || '',
          lastName: marriage?.groom?.grandParents?.maternalMother?.lastName || '',
          birthplace: marriage?.groom?.grandParents?.maternalMother?.birthplace || '',
        },
      },
      witnesses: [
        {
          name: marriage?.groom?.witnesses?.[0]?.name || '',
          idCardType: marriage?.groom?.witnesses?.[0].idCardType,
          idCard: marriage?.groom?.witnesses?.[0]?.idCard || '',
          address: marriage?.groom?.witnesses?.[0]?.address || '',
          areaCode: marriage?.groom?.witnesses?.[0]?.areaCode || '',
          phone: marriage?.groom?.witnesses?.[0]?.phone || '',
          email: marriage?.groom?.witnesses?.[0]?.email || '',
        },
        {
          name: marriage?.groom?.witnesses?.[1]?.name || '',
          idCardType: marriage?.groom?.witnesses?.[1].idCardType,
          idCard: marriage?.groom?.witnesses?.[1]?.idCard || '',
          address: marriage?.groom?.witnesses?.[1]?.address || '',
          areaCode: marriage?.groom?.witnesses?.[1]?.areaCode || '',
          phone: marriage?.groom?.witnesses?.[1]?.phone || '',
          email: marriage?.groom?.witnesses?.[1]?.email || '',
        },
      ],
    },
    bride: {
      id: marriage?.bride?.id || 0,
      firstName: marriage?.bride?.firstName || '',
      lastName: marriage?.bride?.lastName || '',
      idCardType: marriage?.bride.idCardType,
      idCard: marriage?.bride?.idCard || '',
      civilState: marriage?.bride?.civilState || '',
      tribe: marriage?.bride?.tribe || '',
      address: marriage?.bride?.address || '',
      apartment: marriage?.bride?.apartment || '',
      locality: marriage?.bride?.locality || '',
      country: marriage?.bride?.country || '',
      birthplace: marriage?.bride?.birthplace || '',
      birthdate: marriage?.bride?.birthdate || '',
      areaCode: marriage?.bride?.areaCode || '',
      phone: marriage?.bride?.phone || '',
      email: marriage?.bride?.email || '',
      community: marriage?.bride?.community || '',
      job: marriage?.bride?.job || '',
      parents: {
        father: {
          name: marriage?.bride?.parents?.father?.name || '',
          lastName: marriage?.bride?.parents?.father?.lastName || '',
          birthplace: marriage?.bride?.parents?.father?.birthplace || '',
          birthdate: marriage?.bride?.parents?.father?.birthdate || '',
          idCardType: marriage?.bride?.parents?.father.idCardType,
          idCard: marriage?.bride?.parents?.father?.idCard || '',
          civilState: marriage?.bride?.parents?.father?.civilState || '',
          tribe: marriage?.bride?.parents?.father?.tribe || '',
          alive: marriage?.bride?.parents?.father?.alive || true,
          cementery: marriage?.bride?.parents?.father?.cementery || '',
        },
        mother: {
          name: marriage?.bride?.parents?.mother?.name || '',
          lastName: marriage?.bride?.parents?.mother?.lastName || '',
          birthplace: marriage?.bride?.parents?.mother?.birthplace || '',
          birthdate: marriage?.bride?.parents?.mother?.birthdate || '',
          idCardType: marriage?.bride?.parents?.mother.idCardType,
          idCard: marriage?.bride?.parents?.mother?.idCard || '',
          civilState: marriage?.bride?.parents?.mother?.civilState || '',
          tribe: marriage?.bride?.parents?.mother?.tribe || '',
          alive: marriage?.bride?.parents?.mother?.alive || true,
          cementery: marriage?.bride?.parents?.mother?.cementery || '',
        },
        weddingTemple: marriage?.bride?.parents?.weddingTemple || '',
        weddingDate: marriage?.bride?.parents?.weddingDate || '',
      },
      grandParents: {
        paternalFather: {
          name: marriage?.bride?.grandParents?.paternalFather?.name || '',
          lastName: marriage?.bride?.grandParents?.paternalFather?.lastName || '',
          birthplace: marriage?.bride?.grandParents?.paternalFather?.birthplace || '',
        },
        paternalMother: {
          name: marriage?.bride?.grandParents?.paternalMother?.name || '',
          lastName: marriage?.bride?.grandParents?.paternalMother?.lastName || '',
          birthplace: marriage?.bride?.grandParents?.paternalMother?.birthplace || '',
        },
        maternalFather: {
          name: marriage?.bride?.grandParents?.maternalFather?.name || '',
          lastName: marriage?.bride?.grandParents?.maternalFather?.lastName || '',
          birthplace: marriage?.bride?.grandParents?.maternalFather?.birthplace || '',
        },
        maternalMother: {
          name: marriage?.bride?.grandParents?.maternalMother?.name || '',
          lastName: marriage?.bride?.grandParents?.maternalMother?.lastName || '',
          birthplace: marriage?.bride?.grandParents?.maternalMother?.birthplace || '',
        },
      },
      witnesses: [
        {
          name: marriage?.bride?.witnesses?.[0]?.name || '',
          idCardType: marriage?.bride?.witnesses?.[0].idCardType,
          idCard: marriage?.bride?.witnesses?.[0]?.idCard || '',
          address: marriage?.bride?.witnesses?.[0]?.address || '',
          areaCode: marriage?.bride?.witnesses?.[0]?.areaCode || '',
          phone: marriage?.bride?.witnesses?.[0]?.phone || '',
          email: marriage?.bride?.witnesses?.[0]?.email || '',
        },
        {
          name: marriage?.bride?.witnesses?.[1]?.name || '',
          idCardType: marriage?.bride?.witnesses?.[1].idCardType,
          idCard: marriage?.bride?.witnesses?.[1]?.idCard || '',
          address: marriage?.bride?.witnesses?.[1]?.address || '',
          areaCode: marriage?.bride?.witnesses?.[1]?.areaCode || '',
          phone: marriage?.bride?.witnesses?.[1]?.phone || '',
          email: marriage?.bride?.witnesses?.[1]?.email || '',
        },
      ],
    },
    ceremony: {
      address: marriage?.ceremony?.address || '',
      locality: marriage?.ceremony?.locality || '',
      religiousDate: marriage?.ceremony?.religiousDate || '',
      civilDate: marriage?.ceremony?.civilDate || '',
      civilLocality: marriage?.ceremony?.civilLocality || '',
      officiant: marriage?.ceremony?.officiant || '',
    },
    comments: marriage?.comments || '',
  }),
);

export const marriagesCementeriesSelector = createSelector([getCementeries], (cementeries) =>
  Object.keys(cementeries).map((key) => ({
    label: cementeries[key],
    value: key,
  })),
);

export const marriagesCivilStatesSelector = createSelector([getCivilStates], (civilStates) =>
  Object.keys(civilStates).map((key) => ({
    label: civilStates[key],
    value: key,
  })),
);

export const marriagesIdCardTypesSelector = createSelector([getIdCardTypes], (idCardTypes) =>
  Object.keys(idCardTypes).map((key) => ({
    label: idCardTypes[key],
    value: key,
  })),
);

export const marriagesTribesSelector = createSelector([getTribes], (tribes) =>
  Object.keys(tribes).map((key) => ({
    label: tribes[key],
    value: key,
  })),
);

export const marriagesFilesSelector = createSelector([getSelectedItem], (marriage) => ({
  groomIdCardFile: marriage?.groom?.idCardFile || '',
  groomIdCardBackFile: marriage?.groom?.idCardBackFile || '',
  groomGetFile: marriage?.groom?.getFile || '',
  groomParentsFatherIdCardFile: marriage?.groom?.parents?.father?.idCardFile || '',
  groomParentsFatherIdCardBackFile: marriage?.groom?.parents?.father?.idCardBackFile || '',
  groomParentsFatherGetFile: marriage?.groom?.parents?.father?.getFile || '',
  groomParentsMotherIdCardFile: marriage?.groom?.parents?.mother?.idCardFile || '',
  groomParentsMotherIdCardBackFile: marriage?.groom?.parents?.mother?.idCardBackFile || '',
  groomParentsMotherGetFile: marriage?.groom?.parents?.mother?.getFile || '',
  groomParentsKetubaFile: marriage?.groom?.parents?.ketubaFile || '',
  groomParentsMarriageCertificateFile: marriage?.groom?.parents?.marriageCertificateFile || '',
  groomGrandParentsPaternalKetubaFile: marriage?.groom?.grandParents?.paternalKetubaFile || '',
  groomGrandParentsPaternalMarriageCertificateFile:
    marriage?.groom?.grandParents?.paternalMarriageCertificateFile || '',
  groomGrandParentsMaternalKetubaFile: marriage?.groom?.grandParents?.maternalKetubaFile || '',
  groomGrandParentsMaternalMarriageCertificateFile:
    marriage?.groom?.grandParents?.maternalMarriageCertificateFile || '',
  groomWitnessesIdCardFiles: marriage?.groom?.witnesses?.map(({ idCardFile }) => idCardFile) || [],
  groomWitnessesIdCardBackFiles:
    marriage?.groom?.witnesses?.map(({ idCardBackFile }) => idCardBackFile) || [],
  groomWitnessesKetubaFiles: marriage?.groom?.witnesses?.map(({ ketubaFile }) => ketubaFile) || [],
  groomWitnessesMarriageCertificateFiles:
    marriage?.groom?.witnesses?.map(({ marriageCertificateFile }) => marriageCertificateFile) || [],
  brideIdCardFile: marriage?.bride?.idCardFile || '',
  brideIdCardBackFile: marriage?.bride?.idCardBackFile || '',
  brideGetFile: marriage?.bride?.getFile || '',
  brideParentsFatherIdCardFile: marriage?.bride?.parents?.father?.idCardFile || '',
  brideParentsFatherIdCardBackFile: marriage?.bride?.parents?.father?.idCardBackFile || '',
  brideParentsFatherGetFile: marriage?.bride?.parents?.father?.getFile || '',
  brideParentsMotherIdCardFile: marriage?.bride?.parents?.mother?.idCardFile || '',
  brideParentsMotherIdCardBackFile: marriage?.bride?.parents?.mother?.idCardBackFile || '',
  brideParentsMotherGetFile: marriage?.bride?.parents?.mother?.getFile || '',
  brideParentsKetubaFile: marriage?.bride?.parents?.ketubaFile || '',
  brideParentsMarriageCertificateFile: marriage?.bride?.parents?.marriageCertificateFile || '',
  brideGrandParentsPaternalKetubaFile: marriage?.bride?.grandParents?.paternalKetubaFile || '',
  brideGrandParentsPaternalMarriageCertificateFile:
    marriage?.bride?.grandParents?.paternalMarriageCertificateFile || '',
  brideGrandParentsMaternalKetubaFile: marriage?.bride?.grandParents?.maternalKetubaFile || '',
  brideGrandParentsMaternalMarriageCertificateFile:
    marriage?.bride?.grandParents?.maternalMarriageCertificateFile || '',
  brideWitnessesIdCardFiles: marriage?.bride?.witnesses?.map(({ idCardFile }) => idCardFile) || [],
  brideWitnessesIdCardBackFiles:
    marriage?.bride?.witnesses?.map(({ idCardBackFile }) => idCardBackFile) || [],
  brideWitnessesKetubaFiles: marriage?.bride?.witnesses?.map(({ ketubaFile }) => ketubaFile) || [],
  brideWitnessesMarriageCertificateFiles:
    marriage?.bride?.witnesses?.map(({ marriageCertificateFile }) => marriageCertificateFile) || [],
}));
